import React, { useState, useEffect, useContext} from "react";
import Modal from "../common/Modal";
import UIContext from "../../context/UIContext";

function LobbyRooms({lobbyRooms = [], onJoinRoom, joinRoomError, setJoinRoomError}) {

  const [roomFilter, setRoomFilter] = useState("");
  const [hideFullRooms, setHideFullRooms] = useState(false);
  // Local modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [pendingRoomId, setPendingRoomId] = useState(null);
  const [passwordError, setPasswordError] = useState("");
  const [pendingSpectate, setPendingSpectate] = useState(false); // Add local state to track if spectate is pending
  const [expandedRooms, setExpandedRooms] = useState([]);
  const [showInProgressModal, setShowInProgressModal] = useState(false);
  const [pendingInProgressRoom, setPendingInProgressRoom] = useState(null);

  const { setShowCreateRoomModal } = useContext(UIContext); // allow opening the Create Room modal from here

  // tick used to update elapsed timers
  const [nowTs, setNowTs] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const parseCreatedAt = (room) => {
    // accept multiple possible timestamp keys
    const keys = ['createdAt','created','createdUtc','created_on','createdAtUtc','createdAtUtcString','created_time'];
    for (const k of keys) {
      if (room[k]) return new Date(room[k]);
    }
    // fallback: maybe server puts created in meta
    if (room.meta && room.meta.createdAt) return new Date(room.meta.createdAt);
    return null;
  };

  const formatElapsed = (ms) => {
    if (ms < 0) ms = 0;
    const totalSecs = Math.floor(ms / 1000);
    const hours = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    if (hours > 0) return `${String(hours).padStart(2,'0')}:${String(mins).padStart(2,'0')}:${String(secs).padStart(2,'0')}`;
    return `${String(mins).padStart(2,'0')}:${String(secs).padStart(2,'0')}`;
  };

  const formatCreatedUTC = (d) => {
    if (!d) return '';
    try {
      // Format as: "17th Dec 2025 21:38:23"
      const dayNum = d.getUTCDate();
      const daySuffix = (n) => {
        const v = n % 100;
        if (v >= 11 && v <= 13) return 'th';
        switch (n % 10) {
          case 1:
            return 'st';
          case 2:
            return 'nd';
          case 3:
            return 'rd';
          default:
            return 'th';
        }
      };
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[d.getUTCMonth()];
      const year = d.getUTCFullYear();
      const hh = String(d.getUTCHours()).padStart(2, '0');
      const mm = String(d.getUTCMinutes()).padStart(2, '0');
      const ss = String(d.getUTCSeconds()).padStart(2, '0');
      return `${dayNum}${daySuffix(dayNum)} ${month} ${year} ${hh}:${mm}:${ss}`;
    } catch (e) {
      try {
        return d.toUTCString();
      } catch (e2) {
        return String(d);
      }
    }
  };

  // Filter rooms by filter box first, then hide full rooms
  let filteredRooms = lobbyRooms;
  if (
    roomFilter &&
    typeof roomFilter === "string" &&
    roomFilter.trim() !== ""
  ) {
    const filter = roomFilter.trim().toLowerCase();
    filteredRooms = filteredRooms.filter((room) => {
      const name = (
        room.roomName ||
        "" + room.roomId ||
        room.id ||
        ""
      ).toLowerCase();
      const gameType = (room.gameTypeName || room.gameType || "").toLowerCase();
      const host = (
        room.hostUsername ||
        (room.players && room.players[0]?.username) ||
        ""
      ).toLowerCase();
      return (
        name.includes(filter) ||
        gameType.includes(filter) ||
        host.includes(filter)
      );
    });
  }
  if (hideFullRooms) {
    filteredRooms = filteredRooms.filter((room) => {
      const maxPlayers = room.maxPlayers ?? 2;
      return room.players ? room.players.length < maxPlayers : true;
    });
  }

  // Handler for join button
  const getRoomStatus = (room) => {
    // Treat Play Again phase as 'setting up'
    if (room.phase === "playAgain") return "setting up";
    return room.status || (room.started ? "started" : "setting up");
  };

  const canJoinInProgress = (room) => !!(room?.allowJoinInProgress || room?.playersCanJoinAnytime);

  const handleJoinClick = (room, isSpectating) => {
    const status = getRoomStatus(room);
    if (!isSpectating && status === "started" && !canJoinInProgress(room)) {
      setPendingInProgressRoom(room);
      setShowInProgressModal(true);
      return;
    }
    if (room.passworded || room.password || room.hasPassword) {
      if (setJoinRoomError) setJoinRoomError("");
      setPasswordError("");
      setShowPasswordModal(true);
      setPasswordInput("");
      setPendingRoomId(room.roomId || room.id);
      setPendingSpectate(!!isSpectating);
    } else {
      onJoinRoom(room.roomId || room.id, undefined, isSpectating);
    }
  };

  

  // Handler for modal submit
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (pendingRoomId) {
      if (setJoinRoomError) setJoinRoomError("");
      if (pendingSpectate) {
        if (onJoinRoom) {
          onJoinRoom(pendingRoomId, passwordInput, true);
        }
      } else {
        onJoinRoom(pendingRoomId, passwordInput, false);
      }
      setPendingSpectate(false);
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordError("");
    setPasswordInput("");
    setPendingRoomId(null);
    setPendingSpectate(false);
    if (setJoinRoomError) setJoinRoomError("");
  };

  const toggleRoomExpanded = (roomId) => {
    setExpandedRooms((prev) => (
      prev.includes(roomId)
        ? prev.filter((id) => id !== roomId)
        : [...prev, roomId]
    ));
  };

  // Listen for joinRoomError prop and show error in modal
  useEffect(() => {
    if (joinRoomError && showPasswordModal) {
      setPasswordError(joinRoomError);
    }
  }, [joinRoomError, showPasswordModal]);

  return (
    
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Game Rooms</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {filteredRooms.length} room{filteredRooms.length === 1 ? '' : 's'} active
          </p>
        </div>
        <div className="flex gap-2">
          {lobbyRooms.length > 0 && (
            <button
              className="rounded-full border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition hover:border-gray-400 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:border-gray-600 dark:hover:bg-gray-800"
              onClick={() => setHideFullRooms((v) => !v)}
            >
              {hideFullRooms ? "Show All Rooms" : "Hide Full Rooms"}
            </button>
          )}
          {/* Create Room button removed; now handled in Lobby.jsx */}
        </div>
      </div>
      {/* Room Filter Box - only show if rooms exist */}
      {lobbyRooms.length > 0 && (
        <div className="flex items-center">
          <input
            type="text"
            maxLength={20}
            value={roomFilter}
            onChange={(e) => setRoomFilter(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-500 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-400 dark:focus:border-sky-500 dark:focus:ring-sky-950"
            placeholder="Filter rooms by name, game type, or host..."
          />
        </div>
      )}
      {filteredRooms.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {filteredRooms.map((room) => {
            const maxPlayers = room.maxPlayers ?? 2;
            const playerCount = room.players ? room.players.length : 0;
            const spectatorCount = room.spectatorCount ?? room.spectators?.length ?? 0;
            const isFull = playerCount >= maxPlayers;
            const status = getRoomStatus(room);
            const joinLockedByProgress = status === 'started' && !canJoinInProgress(room);
            const allowSpectate = room.allowSpectators;
            const createdDate = parseCreatedAt(room);
            const elapsed = createdDate ? formatElapsed(nowTs - createdDate.getTime()) : null;
            const createdAtUtcStr = createdDate ? formatCreatedUTC(createdDate) : '';
            const roomLabel = room.roomName ? room.roomName : room.roomId || room.id;
            const gameLabel = room.gameDisplayName || room.gameTypeName || room.gameType || 'Game';
            const visiblePlayers = (room.players || []).slice(0, 3);
            const remainingPlayers = Math.max((room.players || []).length - visiblePlayers.length, 0);
            const roomKey = room.roomId || room.id;
            const isExpanded = expandedRooms.includes(roomKey);
            return (
              <li
                key={roomKey}
                className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
              >
                <div
                  role="button"
                  tabIndex={0}
                  className="grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 p-4 outline-none"
                  onClick={() => toggleRoomExpanded(roomKey)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleRoomExpanded(roomKey);
                    }
                  }}
                >
                  <div className="min-w-0 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="truncate">{roomLabel}</span>                      
                      {elapsed && (
                        <span
                          className=""
                          title={`Created (UTC): ${createdAtUtcStr}`}
                        >
                          {elapsed}
                        </span>
                      )}
                                        
                      {(room.passworded || room.password || room.hasPassword) && (
                        <span
                          title="Password protected"
                          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600 dark:bg-rose-950 dark:text-rose-300"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"><path d="M5 13a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2z"/><path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0-2 0m-3-5V7a4 4 0 1 1 8 0v4"/></g></svg>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="row-span-2 flex items-center gap-2 self-center">
                    <button
                      type="button"
                      className={`flex h-10 p-3 items-center justify-center rounded-2xl text-white transition ${isFull || joinLockedByProgress ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-600'}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!isFull) handleJoinClick(room, false);
                      }}
                      title={
                        isFull
                          ? 'Room full'
                          : joinLockedByProgress
                          ? 'Game in progress'
                          : 'Join'
                      }
                      aria-label={
                        isFull
                          ? 'Room full'
                          : joinLockedByProgress
                          ? 'Game in progress'
                          : 'Join room'
                      }
                      disabled={isFull}
                    >
                      <span className="hidden sm:inline">Join&nbsp;</span>
                      <span>{playerCount}/{maxPlayers}</span>
                    </button>
                          {/* In-progress modal */}
                          <Modal
                            show={showInProgressModal}
                            title="Game In Progress"
                            onClose={() => setShowInProgressModal(false)}
                          >
                            <div className="w-full max-w-md mx-auto bg-gray-100 dark:bg-gray-800 p-4 rounded shadow flex flex-col items-center">
                              <div className="mb-4 text-center text-gray-800 dark:text-gray-100">
                                This game is already in progress.<br />
                                You can spectate now and, if the host allows, join as a player in the next game.
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  className="bg-gray-400 text-white p-2 rounded hover:bg-gray-500 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                                  onClick={() => setShowInProgressModal(false)}
                                >
                                  Close
                                </button>
                                <button
                                  type="button"
                                  className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 dark:bg-blue-700 dark:hover:bg-blue-600"
                                  onClick={() => {
                                    setShowInProgressModal(false);
                                    if (pendingInProgressRoom) handleJoinClick(pendingInProgressRoom, true);
                                  }}
                                >
                                  Spectate
                                </button>
                              </div>
                            </div>
                          </Modal>
                    {allowSpectate && (
                      <button
                        type="button"
                        className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-500 text-white transition hover:bg-sky-600"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleJoinClick(room, true);
                        }}
                        title={isFull ? 'Spectate only' : 'Spectate'}
                        aria-label={isFull ? 'Spectate only' : 'Spectate room'}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M2 12s3.5-6 10-6s10 6 10 6s-3.5 6-10 6S2 12 2 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/></svg>
                      </button>
                    )}
                  </div>
                  <div className="min-w-0 text-sm text-sky-700 dark:text-sky-300">
                    <span className="truncate">{gameLabel}</span>
                    

                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-100 px-4 pb-4 pt-3 dark:border-gray-700 relative">
                    {/* Spectator count at top right */}
                    {allowSpectate !== false && (
                      <span className="absolute top-3 right-4 flex items-center gap-1 text-xs font-semibold text-sky-700 dark:text-sky-300">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><ellipse cx="12" cy="12" rx="9.5" ry="6.5" stroke="currentColor" strokeWidth="2" fill="none"/><circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none"/></svg>
                        {spectatorCount}
                      </span>
                    )}
                    {/* Status at top left, capitalized */}
                    <span className={`absolute top-3 left-4 rounded-full px-3 text-xs font-bold ${status === 'started' ? 'text-emerald-700  dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}
                      style={{textTransform: 'capitalize', letterSpacing: '0.01em' }}
                    >
                      {status.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </span>
                    {/* Player usernames moved up */}
                    {visiblePlayers.length > 0 && (
                      <div className="mt-8 flex flex-wrap items-center gap-2">
                        
                        {visiblePlayers.map((player) => (
                          <span
                            key={player.id || player.userId || player.socketId || player.username}
                            className="max-w-full truncate rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-700 dark:border-gray-700 dark:text-gray-200"
                          >
                            {player.username}
                          </span>
                        ))}
                        {remainingPlayers > 0 && (
                          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-500 dark:bg-gray-900 dark:text-gray-400">
                            +{remainingPlayers} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
          {roomFilter && roomFilter.trim() !== ""
            ? (
              <>
                No rooms found matching your search.{' '}
                <button
                  type="button"
                  className="font-medium text-sky-600 underline hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300"
                  onClick={() => setShowCreateRoomModal && setShowCreateRoomModal(true)}
                >
                  Create a game room
                </button>
              </>
            )
            : (
              <>
                No game rooms available.{' '}
                <button
                  type="button"
                  className="font-medium text-sky-600 underline hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300"
                  onClick={() => setShowCreateRoomModal && setShowCreateRoomModal(true)}
                >
                  Create a game room
                </button>
              </>
            )
          }
        </p>
      )}

      {/* Password modal */}
      <Modal
        show={showPasswordModal}
        title="Enter Room Password"
        onClose={closePasswordModal}
      >
        <div className="w-full max-w-md mx-auto bg-gray-100 dark:bg-gray-800 p-4 rounded shadow flex flex-col">
        <form
          onSubmit={handlePasswordSubmit}
          autoComplete="off"
          spellCheck="false"
        >
          <input
            type="password"
            name="room-password"
            className="border p-2 mb-2 w-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
            value={passwordInput}
            onChange={(e) => {
              setPasswordInput(e.target.value);
              setPasswordError("");
            }}
            autoFocus
            autoComplete="new-password"
          />
          {passwordError && (
            <div className="text-red-500 text-sm mb-2">{passwordError}</div>
          )}
          <div className="flex justify-center gap-2 mt-2">
            <button
              type="button"
              className="bg-gray-400 text-white p-2 rounded hover:bg-gray-500 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
              onClick={closePasswordModal}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 dark:bg-blue-700 dark:hover:bg-blue-600"
            >
              {pendingSpectate ? "Spectate" : "Join Game"}
            </button>
          </div>
        </form>
        </div>
      </Modal>
    
    </div>
  );
}

export default LobbyRooms;
